import { useEffect, useState, useCallback, useRef } from 'react';
import TimelineEntries, { type TimelineItem } from './TimelineEntries';
import { ActionButton } from '../ActionButton/ActionButton';

export type positionType = number[];

interface TimelineProps {
  items: TimelineItem[];
}

const Timeline = ({ items = [] }: TimelineProps) => {
  // Sort items into left/right positions
  const itemsLeft = items.filter((_, index) => index % 2 === 0);
  const itemsRight = items.filter((_, index) => index % 2 !== 0);

  const [windowWidth, setWindowWidth] = useState<number>(0);
  const [linePositions, setLinePositions] = useState<positionType | []>([]);
  const [expanded, setExpanded] = useState<boolean>(false);
  const buttonRef = useRef<HTMLDivElement | null>(null);

  const LIMIT_SM = 6;
  const LIMIT_LG = 3;

  const handlePositions = useCallback(
    (positions: positionType) => {
      setLinePositions((prev) => {
        // Add all positions + prev state linePositions, sort them and then
        // strip all but first and last and update state.
        // If no change, there will be no re-render.
        const posArray = positions.concat(prev).sort((a, b) => a - b);
        if (prev.toString() === posArray.toString()) return prev;
        return [posArray[0], posArray[posArray.length - 1]];
      });
    },
    [linePositions]
  );

  const getWindowWidth = () => {
    return window.innerWidth;
  };

  const toggleExpanded = () => {
    setLinePositions([]);
    setExpanded(!expanded);
    setTimeout(() => {
      if (expanded) {
        // Note: smooth scroll seems to be broken in Chrome but working in FF
        buttonRef.current?.scrollIntoView({
          block: 'center',
          behavior: 'smooth',
        });
      }
    }, 0);
  };

  useEffect(() => {
    // Something up the chain (Builder/Next?) seems to force a re-render at window resize.
    // There should really be a listener for the event in this component but seems
    // unneccessary as the useEffect is firing on resize.
    setWindowWidth((prev) => {
      const width = getWindowWidth();
      if (width) return width;
      return prev;
    });
  }, []);

  return (
    <>
      {/*Desktop view (2 columns)*/}
      {windowWidth >= 768 && (
        <div className="relative my-4 flex w-full items-start">
          {/* Timeline */}
          <div
            className={`bg-dark-100 absolute right-0 left-0 m-auto w-[2px]`}
            // Dynamic position not posible with Tailwind so using style
            style={{
              top: `${linePositions ? linePositions[0] : 0}px`,
              height: `${linePositions ? linePositions[1] : 0}px`,
            }}
            aria-hidden="true"
          ></div>
          {/*TimelineEntries (left & right) */}
          <TimelineEntries
            cb={handlePositions}
            entries={itemsLeft}
            anchor="right"
            width="50%"
            limit={!expanded ? LIMIT_LG : 0}
          />
          <TimelineEntries
            cb={handlePositions}
            entries={itemsRight}
            anchor="left"
            width="50%"
            limit={!expanded ? LIMIT_LG : 0}
            padTop="80px"
          />
        </div>
      )}

      {/* Mobile/tablet view (single column) */}
      {windowWidth > 0 && windowWidth < 768 && (
        <div className="relative my-4 flex w-full items-start justify-between">
          {/* Timeline */}
          <div
            className={`bg-dark-100 absolute left-0 m-auto w-[2px]`}
            // TODO: make this dymamic class when Tialwind version bumped
            style={{
              top: `${linePositions ? linePositions[0] : 0}px`,
              height: `${linePositions ? linePositions[1] : 0}px`,
            }}
            aria-hidden="true"
          ></div>
          {/* TimelineEntries */}
          <TimelineEntries
            cb={handlePositions}
            entries={items}
            anchor="left"
            width="100%"
            limit={!expanded ? LIMIT_SM : 0}
          />
        </div>
      )}
      <div className="mb-regular flex justify-center" ref={buttonRef}>
        <ActionButton className="max-w-63" onClick={toggleExpanded}>
          {!expanded ? 'Expand' : 'Collapse'} timeline
        </ActionButton>
      </div>
    </>
  );
};

export default Timeline;
